import { DrawerEvent } from "@quote/quote/eventEnums";
import { useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { useCsrfToken } from "@/hooks";
import useScrollLock from "@/hooks/useScrollLock";

import styles from "./QuoteDrawer.module.css";
import App from "./app";
import { useQuoteStore } from "./quote/store/useQuoteStore";
import { AppTabKey } from "./types";

const QuoteDrawer = () => {
  const [closing, setClosing] = useState(false);
  const [packageId, setPackageId] = useState<number | null>(null);
  const [groupSize, setGroupSize] = useState<number>(0);
  const [arrivalDate, setArrivalDate] = useState<string>("");
  const [drawerId, setDrawerId] = useState<string>("");

  const {
    resetArrivalState,
    resetAirportState,
    resetFlightTimeState,
    resetRoomsState,
    setActiveTab,
    resetAppState,
  } = useQuoteStore(
    useShallow((state) => ({
      resetArrivalState: state.arrivalActions.resetState,
      resetAirportState: state.airportActions.resetState,
      resetFlightTimeState: state.flightTimeActions.resetState,
      resetRoomsState: state.roomsActions.resetState,
      ...state.appActions,
    })),
  );

  useCsrfToken();

  const resetState = () => {
    setPackageId(0);
    setGroupSize(0);
    setArrivalDate("");
    setDrawerId("");
  };

  const close = useCallback(() => {
    resetArrivalState();
    resetAirportState();
    resetFlightTimeState();
    resetRoomsState();
    resetAppState();
    setClosing(true);
  }, [
    resetArrivalState,
    resetAirportState,
    resetFlightTimeState,
    resetRoomsState,
    resetAppState,
  ]);

  const handleOpenDrawer = useCallback(
    (event: any) => {
      const { packageId, groupSize, arrivalDate, hasFlight } = event ?? {};
      const newPackageId = parseInt(packageId, 10);
      const newGroupSize = groupSize ? parseInt(groupSize, 10) : null;

      if (isNaN(newPackageId)) {
        console.warn("Invalid package id received", event);
        return;
      }

      if (hasFlight) {
        setActiveTab(AppTabKey.HOTEL_FLIGHT_GOLF);
      }

      setDrawerId(Math.random().toString(36).slice(2, 10));
      setPackageId(newPackageId);

      if (newGroupSize) setGroupSize(newGroupSize);

      if (arrivalDate) setArrivalDate(arrivalDate);
    },
    [setActiveTab],
  );

  useScrollLock(!!packageId);

  useEffect(() => {
    const eventHandler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) {
        return;
      }

      const { eventType, event } = e.data ?? {};
      if (!eventType) return;

      if (eventType === DrawerEvent.EXIT) {
        close();
        return;
      }

      if (eventType === DrawerEvent.OPEN_DRAWER && event?.packageId) {
        handleOpenDrawer(event);
      }
    };

    window.addEventListener("message", eventHandler);

    return () => {
      window.removeEventListener("message", eventHandler);
    };
  }, [close, handleOpenDrawer]);

  return (
    <div className={styles.drawer} data-visibility={!!packageId}>
      <div className={styles.overlay} onClick={close} data-closing={closing} />
      <div
        id="drawer-content"
        className={styles.drawerContent}
        onAnimationEnd={() => {
          if (closing) {
            resetState();
            setClosing(false);
          }
        }}
        data-closing={closing}
      >
        {packageId ? (
          <App
            packageId={packageId}
            groupSize={groupSize}
            arrivalDate={arrivalDate}
            drawerId={drawerId}
          />
        ) : (
          <div>❗Error...</div>
        )}
      </div>
    </div>
  );
};

export default QuoteDrawer;
